import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Storefront, CircleNotch, CheckCircle, Plug, Info, MagnifyingGlass, Copy, ArrowsClockwise, PlugsConnected, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { buildSnippets } from "@/lib/integrationSnippets";

function WixAppStatus({ onStatus, onEditKey, onDisconnected }) {
  const [st, setSt] = useState(null);
  const [orders, setOrders] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  useEffect(() => {
    api.get("/wix/dashboard/status").then((r) => { setSt(r.data); onStatus?.(r.data); }).catch(() => { setSt({ linked: false }); onStatus?.({ linked: false }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (st?.linked) api.get("/wix/dashboard/orders", { params: { limit: 10 } }).then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([]));
  }, [st?.linked]);
  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/wix/dashboard/sync-all");
      toast.success(`Sincronizados ${data.synced}/${data.total} productos al catálogo de Wix${data.failed ? ` (${data.failed} con error)` : ""}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };
  const importOrders = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/wix/dashboard/import-orders");
      toast.success(`Importados ${data.imported} pedidos de Wix (${data.clients_added} clientes nuevos, ${data.skipped} ya existían)`);
    } catch (e) { toast.error(apiError(e)); } finally { setImporting(false); }
  };
  const disconnectWix = async () => {
    if (!window.confirm("¿Desconectar Wix? Tus pedidos nuevos se guardarán en tu tienda Hephy.")) return;
    try { await api.post("/wix/dashboard/disconnect"); toast.success("Wix desconectado"); setSt({ linked: false }); onStatus?.({ linked: false }); onDisconnected?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!st) return null;
  return (
    <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mb-6" data-testid="wix-app-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><PlugsConnected size={22} weight="duotone" className="text-gold" /> <h3 className="font-semibold">App de Wix (tienda conectada)</h3></div>
        {st.linked
          ? <span className="flex items-center gap-1 text-emerald-400 text-sm" data-testid="wix-app-connected"><CheckCircle weight="fill" /> Conectado</span>
          : <span className="text-neutral-500 text-sm" data-testid="wix-app-not-connected">No conectado</span>}
      </div>
      {st.linked ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white/5 rounded-lg p-3"><p className="text-[11px] text-neutral-500">Sitio Wix</p><p className="text-neutral-200 font-mono text-xs truncate" data-testid="wix-app-site">{st.site_url || st.site_id || "—"}</p></div>
            <div className="bg-white/5 rounded-lg p-3"><p className="text-[11px] text-neutral-500">Tipo de conexion</p><p className="text-neutral-200" data-testid="wix-app-conn-type">{st.connection === "api_key" ? "API Key" : "App de Wix (App Market)"}</p></div>
          </div>
          <p className="text-[11px] text-neutral-500" data-testid="wix-app-synced-count"><b className="text-neutral-300">{st.synced_products ?? 0}</b> productos sincronizados al catalogo de Wix</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={syncAll} disabled={syncing} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="wix-app-sync-all">
              {syncing ? <CircleNotch className="animate-spin" /> : <><ArrowsClockwise className="mr-1" /> Sincronizar productos al catálogo de Wix</>}
            </Button>
            <Button onClick={importOrders} disabled={importing} variant="outline" className="rounded-full border-white/15" data-testid="wix-app-import-orders">
              {importing ? <CircleNotch className="animate-spin" /> : <><Receipt className="mr-1" /> Importar pedidos de Wix a Hephy</>}
            </Button>
            {st.connection === "api_key" && (
              <Button onClick={() => onEditKey?.()} variant="ghost" className="rounded-full text-neutral-400" data-testid="wix-app-edit-key">Editar clave API</Button>
            )}
            <Button onClick={disconnectWix} variant="outline" className="rounded-full border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="wix-disconnect-btn">Desconectar Wix</Button>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><Receipt size={16} className="text-gold" /> Pedidos recientes de tu tienda Wix</div>
            {orders === null ? <p className="text-xs text-neutral-500">Cargando pedidos…</p>
              : orders.length === 0 ? <p className="text-xs text-neutral-500" data-testid="wix-app-orders-empty">Aún no hay pedidos en tu tienda Wix.</p>
              : <div className="space-y-2" data-testid="wix-app-orders">
                  {orders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-xs">
                      <div><span className="text-neutral-200 font-medium">#{o.number}</span> <span className="text-neutral-500">· {o.buyer}</span></div>
                      <div className="flex items-center gap-3"><span className="text-neutral-400">{o.items} art.</span><span className="text-gold font-semibold">{o.total}</span><span className="text-neutral-500">{o.payment_status}</span></div>
                    </div>
                  ))}
                </div>}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 text-xs text-neutral-400 bg-white/5 rounded-lg p-3">
          <Info size={16} className="text-gold shrink-0 mt-0.5" />
          <span>Esta cuenta aún no está vinculada a un sitio Wix vía la app del App Market. Abre el panel de Hephy 3D desde <b>Apps → Hephy 3D</b> dentro de tu sitio Wix para vincularlo automáticamente.</span>
        </div>
      )}
    </div>
  );
}

const CodeBlock = ({ code, label, testid }) => {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const copy = () => { try { navigator.clipboard.writeText(code); setDone(true); toast.success(t("integrations.copied")); setTimeout(() => setDone(false), 1500); } catch { toast.error("Error"); } };
  return (
    <div className="relative">
      <pre className="code-snippet bg-black/60 rounded-lg p-3 pr-24 font-mono text-[11px] text-neutral-300 overflow-x-auto whitespace-pre max-h-72" data-testid={testid}>{code}</pre>
      <button onClick={copy} data-testid={`${testid}-copy`} className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] text-gold hover:underline bg-black/70 rounded px-2 py-1">
        <Copy size={12} /> {done ? t("integrations.copied") : (label || t("integrations.copyBtn"))}
      </button>
    </div>
  );
};

function AthmCard() {
  const [st, setSt] = useState(null);
  const [pub, setPub] = useState("");
  const [priv, setPriv] = useState("");
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/athm/status").then((r) => setSt(r.data)).catch(() => setSt({ connected: false }));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!pub.trim() || !priv.trim()) { toast.error("Ingresa el Public Token y el Private Token."); return; }
    setSaving(true);
    try { await api.put("/athm/connect", { public_token: pub.trim(), private_token: priv.trim() }); toast.success("ATH Móvil conectado"); setPub(""); setPriv(""); setEdit(false); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const disconnect = async () => { try { await api.delete("/athm/connect"); toast.success("ATH Móvil desconectado"); load(); } catch (e) { toast.error(apiError(e)); } };
  const connected = st?.connected;
  return (
    <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mb-6" data-testid="athm-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">ATH Móvil Business</h3>
          <p className="text-sm text-neutral-400">Cobra por ATH Móvil en tu tienda (Puerto Rico), además de tarjeta.</p>
        </div>
        {connected
          ? <span className="flex items-center gap-1 text-emerald-400 text-sm" data-testid="athm-connected"><CheckCircle weight="fill" /> Conectado</span>
          : <span className="text-neutral-500 text-sm" data-testid="athm-not-connected">No conectado</span>}
      </div>
      {connected && !edit ? (
        <div className="mt-4 flex items-center gap-3">
          <p className="text-sm text-neutral-300">Public Token: <span className="font-mono">••••{st.public_token_last4}</span></p>
          <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => setEdit(true)} data-testid="athm-edit">Cambiar tokens</Button>
          <Button size="sm" variant="ghost" className="rounded-full text-neutral-400" onClick={disconnect} data-testid="athm-disconnect">Desconectar</Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div><Label>Public Token</Label><Input value={pub} onChange={(e) => setPub(e.target.value)} placeholder="Token público de ATH Business" className="mt-1 bg-white/5 border-white/15 font-mono" data-testid="athm-public" /></div>
          <div><Label>Private Token</Label><Input type="password" value={priv} onChange={(e) => setPriv(e.target.value)} placeholder="Token privado (se guarda en el servidor)" className="mt-1 bg-white/5 border-white/15 font-mono" data-testid="athm-private" /></div>
          <p className="text-[11px] text-neutral-500">Los encuentras en la app ATH Business → Configuración. El token privado nunca se muestra en el navegador del cliente. ATH Móvil no tiene ambiente de pruebas: valida con una transacción real de bajo monto.</p>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="athm-save">{saving ? "Guardando…" : "Conectar ATH Móvil"}</Button>
            {edit && <Button variant="ghost" className="rounded-full text-neutral-400" onClick={() => setEdit(false)}>Cancelar</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Integrations() {
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const [ints, setInts] = useState({});
  const [shopStatus, setShopStatus] = useState({});
  const [shopDomain, setShopDomain] = useState("");
  const [wix, setWix] = useState({ api_key: "", account_id: "", site_id: "", site_origin: "" });
  const [wixSt, setWixSt] = useState(null);
  const [editWixKey, setEditWixKey] = useState(false);
  const [wixSites, setWixSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [saving, setSaving] = useState(null);
  const snips = buildSnippets(window.location.origin);

  const load = () => api.get("/integrations").then((r) => setInts(r.data)).catch(() => {});
  const loadShopify = () => api.get("/shopify/status").then((r) => setShopStatus(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadShopify();
    const q = new URLSearchParams(window.location.search).get("shopify");
    if (q === "connected") toast.success("Shopify conectado");
    else if (q === "error") toast.error("No se pudo conectar Shopify. Revisa el dominio y las URLs de la app.");
    if (q) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const connectShopify = async () => {
    setSaving("shopify");
    try {
      const { data } = await api.post("/shopify/connect", { shop: shopDomain });
      window.location.assign(data.authorize_url);
    } catch (e) { toast.error(apiError(e)); setSaving(null); }
  };
  const disconnectShopify = async () => {
    if (!window.confirm("¿Desconectar Shopify? Tus pedidos nuevos se guardarán en tu tienda Hephy.")) return;
    try { await api.post("/shopify/disconnect"); toast.success("Shopify desconectado"); loadShopify(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveWix = async () => {
    setSaving("wix");
    try { await api.put("/integrations", { platform: "wix", ...wix }); toast.success(t("integrations.wixSaved")); load(); refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(null); }
  };
  const detectSites = async () => {
    setLoadingSites(true);
    try {
      await api.put("/integrations", { platform: "wix", api_key: wix.api_key, account_id: wix.account_id });
      const { data } = await api.get("/integrations/wix/sites");
      setWixSites(data.sites || []);
      if (data.sites?.length) { setWix((w) => ({ ...w, site_id: data.sites[0].id })); toast.success(t("integrations.sitesDetected", { count: data.sites.length })); }
      else toast.error(t("integrations.noSites"));
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingSites(false); }
  };
  const disconnect = async (p) => { await api.delete(`/integrations/${p}`); toast.success(t("integrations.disconnected")); load(); refresh(); };

  const Connected = ({ p }) => ints[p]?.connected ? (
    <span className="flex items-center gap-1 text-emerald-400 text-sm"><CheckCircle weight="fill" /> {t("integrations.connected")}</span>
  ) : <span className="text-neutral-500 text-sm">{t("integrations.notConnected")}</span>;

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-3xl font-bold tracking-tight">{t("integrations.title")}</h1>
      <p className="text-neutral-400 mt-1 mb-6">{t("integrations.subtitle")}</p>

      <Tabs defaultValue="connections">
        <TabsList className="bg-white/5 mb-6" data-testid="integrations-tabs">
          <TabsTrigger value="connections" data-testid="tab-connections">{t("integrations.tabConnections")}</TabsTrigger>
          <TabsTrigger value="instructions" data-testid="tab-instructions">{t("integrations.tabInstructions")}</TabsTrigger>
        </TabsList>

        {/* ── Connections ── */}
        <TabsContent value="connections">
          {/* Wix App Market (embedded app) — unified card */}
          <WixAppStatus onStatus={setWixSt} onEditKey={() => setEditWixKey(true)} onDisconnected={() => { load(); refresh(); }} />
          {/* ATH Móvil Business */}
          <AthmCard />
          {/* Shopify */}
          <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mb-6" data-testid="shopify-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Storefront size={22} weight="duotone" className="text-gold" /> <h3 className="font-semibold">Shopify</h3></div>
              {shopStatus.connected
                ? <span className="flex items-center gap-1 text-emerald-400 text-sm" data-testid="shopify-connected"><CheckCircle weight="fill" /> Conectado</span>
                : <span className="text-neutral-500 text-sm">No conectado</span>}
            </div>
            {shopStatus.connected ? (
              <div className="space-y-3">
                <p className="text-sm text-neutral-300">Tu tienda <b className="text-gold font-mono">{shopStatus.shop}</b> está conectada. Envía tus productos al catálogo de Shopify desde la página de cada producto (botón "Enviar a Shopify").</p>
                <Button variant="outline" onClick={disconnectShopify} className="rounded-full border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="shopify-disconnect">Desconectar Shopify</Button>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-2 text-xs text-neutral-400 bg-white/5 rounded-lg p-3 mb-4">
                  <Info size={16} className="text-gold shrink-0 mt-0.5" />
                  <span>Conecta tu tienda con un clic. Te llevaremos a Shopify para autorizar la app y regresarás aquí automáticamente.</span>
                </div>
                <div className="space-y-3">
                  <div><Label>Dominio de tu tienda Shopify</Label><Input value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} placeholder="mitienda.myshopify.com" className="mt-1 bg-white/5 border-white/15 font-mono" data-testid="shopify-domain" /></div>
                  <Button onClick={connectShopify} disabled={saving === "shopify" || !shopDomain} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="shopify-connect">
                    {saving === "shopify" ? <CircleNotch className="animate-spin" /> : <><Plug className="mr-1" /> Conectar con Shopify</>}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Wix (API-key config) — hidden once connected, unless the user chooses to edit the key */}
          {(!wixSt?.linked || editWixKey) && (
          <div className="border border-white/10 rounded-xl p-6 bg-[#121212]" data-testid="wix-apikey-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Storefront size={22} weight="duotone" className="text-gold" /> <h3 className="font-semibold">Wix — conexión por API Key</h3></div>
              <Connected p="wix" />
            </div>
            <div className="flex items-start gap-2 text-xs text-neutral-400 bg-white/5 rounded-lg p-3 mb-4">
              <Info size={16} className="text-gold shrink-0 mt-0.5" />
              <span>{t("integrations.wixHelp")}</span>
            </div>
            <div className="space-y-3">
              <div><Label>{t("integrations.wixApiKey")}</Label><Input type="password" value={wix.api_key} onChange={(e) => setWix({ ...wix, api_key: e.target.value })} placeholder={ints.wix?.api_key_masked || "IST..."} className="mt-1 bg-white/5 border-white/15 font-mono" data-testid="wix-apikey" /></div>
              <div><Label>{t("integrations.accountId")}</Label><Input value={wix.account_id} onChange={(e) => setWix({ ...wix, account_id: e.target.value })} placeholder={ints.wix?.account_id || "xxxxxxxx-xxxx-..."} className="mt-1 bg-white/5 border-white/15 font-mono" data-testid="wix-account" /></div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label>{t("integrations.site")}</Label>
                  {wixSites.length ? (
                    <select value={wix.site_id} onChange={(e) => setWix({ ...wix, site_id: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-white/5 border border-white/15 px-3 text-sm" data-testid="wix-site-select">
                      {wixSites.map((s) => <option key={s.id} value={s.id} className="bg-[#1a1a1a]">{s.name} — {s.id}</option>)}
                    </select>
                  ) : (
                    <Input value={wix.site_id} onChange={(e) => setWix({ ...wix, site_id: e.target.value })} placeholder={ints.wix?.site_id || "site-id"} className="mt-1 bg-white/5 border-white/15 font-mono" data-testid="wix-site" />
                  )}
                </div>
                <Button onClick={detectSites} disabled={loadingSites || !wix.api_key || !wix.account_id} variant="outline" className="rounded-full border-white/15 shrink-0" data-testid="wix-detect-sites">
                  {loadingSites ? <CircleNotch className="animate-spin" /> : <><MagnifyingGlass className="mr-1" /> {t("integrations.detectSites")}</>}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveWix} disabled={saving === "wix"} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-wix">
                  {saving === "wix" ? <CircleNotch className="animate-spin" /> : <><Plug className="mr-1" /> {t("integrations.connectWix")}</>}
                </Button>
                {ints.wix?.connected && <Button variant="outline" onClick={() => disconnect("wix")} className="rounded-full border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="wix-apikey-disconnect">Desconectar Wix</Button>}
              </div>
            </div>
          </div>
          )}
        </TabsContent>

        {/* ── Instructions ── */}
        <TabsContent value="instructions">
          <div className="space-y-6 text-xs text-neutral-400" data-testid="embed-instructions">
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
              <h3 className="font-semibold text-base text-white mb-1">{t("integrations.instructionsTitle")}</h3>
              <p className="text-neutral-500">{t("integrations.instructionsSubtitle")}</p>
            </div>

            {/* Universal button */}
            <div className="border border-emerald-500/30 rounded-xl p-5 bg-emerald-500/[0.06]" data-testid="ins-universal">
              <p className="font-semibold text-emerald-300 mb-1">{t("integrations.insUniversalTitle")}</p>
              <p className="mb-3 text-neutral-300">{t("integrations.insUniversalNote")}</p>
              <CodeBlock code={snips.universalButton} testid="ins-universal-code" />
            </div>

            {/* Universal embed */}
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]" data-testid="ins-embed">
              <p className="font-semibold text-gold mb-1">{t("integrations.insEmbedTitle")}</p>
              <p className="mb-3 text-neutral-400">{t("integrations.insEmbedNote")}</p>
              <CodeBlock code={snips.embedCode} testid="ins-embed-code" />
            </div>

            {/* Wix options */}
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212] space-y-5">
              <p className="font-semibold text-base text-white">Wix</p>

              <div className="flex items-start gap-2 text-[12px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="ins-wix-velo-req">
                <span>
                  <b>Requisito:</b> tu sitio Wix debe permitir <b>Wix Velo (Dev Mode)</b> e <b>Insertar HTML en modo “Código”</b>. Los sitios creados con <b>Wix Harmony</b> (el editor nuevo simplificado) <b>no permiten insertar código personalizado ni Velo</b>, por lo que las Opciones C y D no funcionan ahí. Si estás en Harmony, usa la <b>Opción A</b> (iframe embebido) o migra el sitio al <b>Editor de Wix</b> clásico con Dev Mode.
                </span>
              </div>

              <div className="text-[12px] text-neutral-300 bg-white/[0.03] border border-white/10 rounded-lg p-3" data-testid="ins-wix-howitworks">
                <p className="font-semibold text-white mb-1">Cómo funciona en conjunto</p>
                <p className="text-neutral-400">Creas una página (o usas la Página de Producto) en TU sitio Wix y ahí embebes el personalizador de Hephy con un bloque <b>Insertar HTML (modo “Código”)</b>. Ese bloque y tu código <b>Velo</b> se comunican por mensajes: el personalizador manda el diseño y Velo lo agrega al <b>carrito nativo de Wix</b> (sin salir de tu tienda ni usar Stripe). El vínculo entre cada producto de Wix y su personalizador es el <b>campo SKU</b>: guarda ahí el <b>ID de Hephy</b> del producto. Así el personalizador/botón <b>solo aparece en los productos que tienen personalización</b> y se oculta en los demás.</p>
              </div>

              <div>
                <p className="font-semibold text-gold mb-1">Opción A · Mostrar el personalizador (iframe)</p>
                <ol className="list-decimal list-inside space-y-0.5 text-neutral-400">
                  <li>Copia el <b>iframe universal</b> (arriba) y reemplaza <code>TU_ID_DE_PRODUCTO</code>.</li>
                  <li>En el <b>Editor de Wix</b>: <b>+ Agregar</b> → <b>Insertar</b> → <b>Insertar HTML</b> (Embed a Widget).</li>
                  <li>Elige <b>“Código”</b>, pega el código y pulsa <b>Aplicar</b>. Ajusta el tamaño y <b>Publica</b>.</li>
                </ol>
              </div>

              <div>
                <p className="font-semibold text-gold mb-1">Opción B · Crear el producto en tu catálogo</p>
                <ol className="list-decimal list-inside space-y-0.5 text-neutral-400">
                  <li>En la pestaña <b>Conexiones</b> guarda tu <b>API Key de Wix</b> y detecta tu sitio.</li>
                  <li>En cada producto, pulsa <b>“Enviar a Wix”</b> para crear/actualizar el producto en Wix Stores.</li>
                  <li>Luego agrega el personalizador con la Opción A.</li>
                </ol>
              </div>

              <div className="border border-gold/20 rounded-lg p-3 bg-gold/5" data-testid="ins-wix-c">
                <p className="font-semibold text-gold mb-1">Opción C · Página de producto dinámica (cada producto muestra SU personalizador)</p>
                <p className="mb-2 text-neutral-400">Usa Velo para que cada producto cargue automáticamente el personalizador correcto y conectar el botón <b>“Añadir al carrito”</b>.</p>
                <ol className="list-decimal list-inside space-y-0.5 text-neutral-400 mb-2">
                  <li>Activa <b>Dev Mode (Velo)</b>.</li>
                  <li>En cada producto de Wix, guarda en el campo <b>SKU</b> el <b>ID</b> de su personalizador (cópialo de la pantalla del producto).</li>
                  <li>Agrega <b>Insertar → Insertar HTML</b> en modo <b>“Código”</b> (NO “Dirección web/URL”) con ID <b>hephyEmbed</b> y pega el <b>HTML del relé</b>.</li>
                  <li>Crea el archivo <b>backend/customCart.web.js</b> con la parte marcada y pega el resto en el code panel de la Página de Producto.</li>
                </ol>
                <div className="flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-400/10 border border-amber-400/20 rounded-lg p-2 mb-3">
                  <span><b>¿Por qué modo “Código”?</b> Los elementos en modo “Dirección web (URL)” no soportan <code>onMessage</code>/<code>postMessage</code> y el carrito nunca conecta. Prueba siempre en el sitio <b>publicado</b>.</span>
                </div>
                <p className="text-neutral-400 mb-1">HTML del relé (dentro del elemento <b>hephyEmbed</b>):</p>
                <CodeBlock code={snips.embedRelayHtml} testid="ins-wix-relay" />
                <p className="text-neutral-400 mb-1 mt-3">Código Velo (Página de Producto + backend):</p>
                <CodeBlock code={snips.veloCode} testid="ins-wix-velo" />
              </div>

              <div className="border border-white/10 rounded-lg p-3" data-testid="ins-wix-d">
                <p className="font-semibold text-gold mb-1">Opción D · Botón “Personalizar” (popup · aparece solo si aplica)</p>
                <p className="mb-2 text-neutral-400">Agrega el botón en la Página de Producto: aparece <b>solo en los productos que tienen personalizador</b> (los que guardaste su ID de Hephy en el <b>SKU</b>) y se oculta en el resto. Al pulsarlo, el cliente personaliza en una ventana y el diseño se envía al carrito de <b>ese</b> producto.</p>
                <ol className="list-decimal list-inside space-y-0.5 text-neutral-400 mb-2">
                  <li>Activa <b>Dev Mode (Velo)</b>.</li>
                  <li>En el <b>SKU</b> de cada producto con personalización guarda su <b>ID de Hephy</b>.</li>
                  <li>Agrega <b>Insertar → Insertar HTML</b> en modo <b>“Código”</b> con ID <b>hephyButton</b>, pega el <b>HTML del botón</b> y marca el elemento como <b>“Colapsado/Oculto al cargar”</b>.</li>
                  <li>Usa el archivo <b>backend/customCart.web.js</b> (el mismo de la Opción C) y pega el código Velo.</li>
                </ol>
                <p className="text-neutral-400 mb-1">HTML del botón (elemento <b>hephyButton</b>):</p>
                <CodeBlock code={snips.popupButtonCode} testid="ins-wix-popup-html" />
                <p className="text-neutral-400 mb-1 mt-3">Código Velo (Página de Producto):</p>
                <CodeBlock code={snips.popupVeloCode} testid="ins-wix-popup-velo" />
                <p className="text-neutral-400 mb-1 mt-3">Archivo backend <code>backend/media.web.js</code> (para que la <b>foto del diseño</b> salga en el carrito):</p>
                <CodeBlock code={snips.mediaImporterCode} testid="ins-wix-media" />
              </div>
            </div>

            {/* Wix — automatizaciones */}
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]" data-testid="ins-wix-auto">
              <p className="font-semibold text-gold mb-2">Wix — automatizaciones</p>

              <p className="text-neutral-300 font-medium mb-1">A) Auto-aplicar el promo del vendedor</p>
              <p className="text-neutral-400 mb-1 text-xs">Pega en <b>masterPage.js</b> (Velo). Cuando el cliente llega por el enlace del vendedor (<code>?promo=</code>), el descuento se aplica solo en el personalizador.</p>
              <CodeBlock code={snips.promoAutoApplyCode} testid="ins-wix-promo" />

              <p className="text-neutral-300 font-medium mb-1 mt-4">B) Botón “Personalizar con Hephy 3D” al editar un producto</p>
              <p className="text-neutral-400 mb-1 text-xs">Enlaza un producto de Wix con un personalizador escribiendo el ID de Hephy en el <b>SKU</b> del producto (así “Personalizar” aparece solo en ese producto).</p>
              <CodeBlock code={snips.dashboardLinkCode} testid="ins-wix-dashboard-link" />

              <p className="text-neutral-300 font-medium mb-1 mt-4">C) Página del Portal del Vendedor en tu dominio</p>
              <p className="text-neutral-400 mb-1 text-xs">Crea una página en tu sitio Wix (ej. “Vendedores”) y pega esto en un bloque <b>Insertar HTML</b> (modo Código). Los vendedores entran con su correo/contraseña.</p>
              <CodeBlock code={snips.sellerPageEmbed} testid="ins-wix-seller-embed" />
            </div>

            {/* Shopify */}
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
              <p className="font-semibold text-gold mb-1">Shopify</p>
              <ol className="list-decimal list-inside space-y-0.5 text-neutral-400">
                <li>Copia el <b>iframe universal</b> (arriba) y reemplaza <code>TU_ID_DE_PRODUCTO</code>.</li>
                <li>En Shopify: <b>Tienda online → Páginas</b> (o el producto), abre el editor y activa <b>“{'<>'} Mostrar HTML”</b>.</li>
                <li>Pega el código y <b>Guarda</b>. También puedes usar un bloque <b>“HTML personalizado”</b> en el tema.</li>
                <li>O usa <b>“Enviar a Shopify”</b> para crear el producto en tu catálogo (requiere tus credenciales en Conexiones).</li>
              </ol>
            </div>

            {/* WordPress / other */}
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
              <p className="font-semibold text-gold mb-1">WordPress y otras webs</p>
              <ol className="list-decimal list-inside space-y-0.5 text-neutral-400">
                <li>Añade un bloque <b>HTML personalizado</b> (WordPress: bloque “HTML personalizado”; Elementor: widget “HTML”).</li>
                <li>Pega el <b>Botón universal</b> (recomendado, el cliente paga en Hephy) o el <b>iframe universal</b> para incrustarlo en la página.</li>
                <li>Reemplaza <code>TU_ID_DE_PRODUCTO</code> por el ID del producto y <code>TU-TIENDA</code> por tu dirección de tienda.</li>
              </ol>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
